import React from "react";
import "@ant-design/compatible/assets/index.css";
import moment from "moment";
import {
  Collapse,
  Form,
  message,
  DatePicker,
  Button,
  Checkbox,
  InputNumber,
} from "antd";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import HmgMainDropdown from "../../components/Common/DataAttachedElements/HmgDropdown";
import ProjectSelect from "../../components/Common/DataAttachedElements/ProjectSelect";
import HotelDropdown from "../../components/Common/DataAttachedElements/HotelDropdown";

import { useGetHmg } from "../../hooks/hmg.hooks";
import { useGetHotelById } from "../../hooks/hotel.hooks";
import { useTriggerEnvelope } from "../../hooks/qcApi.hooks";
import { triggerEnvelopeSchema } from "../../schema/envelope.schema";

import { formItemLayout, formItemLayoutWithOutLabel } from "../../utils/UI";

const { Panel } = Collapse;

const EnvelopTrigger = () => {
  const {
    control,
    watch,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(triggerEnvelopeSchema),
    defaultValues: {
      hmg: undefined,
      project: undefined,
      hotel: undefined,
      businessDate: undefined,
      backDays: 7,
      skipCheck: false,
    },
  });

  const { hmg, project, hotel, businessDate } = watch();
  const { data: hotelsList } = useGetHotelById(hotel, {
    enabled: !!hotel,
  });
  const { data: hmgsList } = useGetHmg(hmg, {
    enabled: !!hmg,
  });

  const { mutate: triggerEnvelope, isLoading: isTriggering } =
    useTriggerEnvelope({
      onSuccess: (response) => {
        message.success(response.message);
        reset(getValues());
      },
      onError: (error) => {
        message.error(error.response?.data?.message ?? "Server Error");
      },
    });

  const submitManualEnvelope = (values) => {
    triggerEnvelope({
      businessDate: moment(values.businessDate).format("MM/DD/YYYY"),
      mgmtGroup: hmgsList.hmg_name,
      hotel: hotelsList.hotel_name,
      backDays: values.backDays,
      skipCheck: values.skipCheck,
      ...(process.env.REACT_APP_PLATFORM_TYPE === "MARRIOTT"
        ? { hotelCode: hotelsList.hotel_code }
        : { hotelId: hotelsList.hotel_id }),
    });
  };

  return (
    <>
      <div className="container_ab">
        <Collapse defaultActiveKey={["1"]} expandIconPosition="end">
          <Panel header="HOTEL ENVELOPE DETAILS" key="1">
            <form onSubmit={handleSubmit(submitManualEnvelope)}>
              <Controller
                name="hmg"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <HmgMainDropdown
                    value={value}
                    onChange={onChange}
                    placeholder="Please Select Hotel Management Group"
                  />
                )}
              />
              <Form.Item label="Project" {...formItemLayout}>
                <Controller
                  name="project"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <ProjectSelect
                      value={value}
                      onChange={onChange}
                      hmgId={hmg}
                      placeholder="Please Select Project"
                      solution="PNA"
                    />
                  )}
                />
              </Form.Item>
              <Controller
                name="hotel"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <HotelDropdown
                    value={value}
                    onChange={onChange}
                    projectId={project}
                    multiple={false}
                  />
                )}
              />
              <Form.Item
                {...formItemLayout}
                label="Business Date"
                required
                help={errors?.businessDate?.message ?? ""}
                validateStatus={errors.businessDate ? "error" : ""}
              >
                <Controller
                  name="businessDate"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <DatePicker
                      data-testid="envelop-trigger-date"
                      disabledDate={(current) => current > moment()}
                      format="YYYY-MM-DD"
                      placeholder="Please Enter Business Date"
                      style={{ width: "100%" }}
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
              </Form.Item>
              <Form.Item {...formItemLayout} label="Back Days">
                <Controller
                  name="backDays"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <InputNumber
                      min={0}
                      precision={0}
                      value={value}
                      onChange={onChange}
                      style={{ width: "15%" }}
                    />
                  )}
                />
              </Form.Item>
              <Form.Item {...formItemLayoutWithOutLabel}>
                <Controller
                  name="skipCheck"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Checkbox
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                    >
                      Skip mandatory report check
                    </Checkbox>
                  )}
                />
              </Form.Item>
              <Form.Item {...formItemLayoutWithOutLabel}>
                <Button
                  data-testid="envelop-trigger-submit"
                  type="primary"
                  htmlType="submit"
                  disabled={
                    !(hotel && businessDate) || isTriggering || !isDirty
                  }
                >
                  Submit
                </Button>
              </Form.Item>
            </form>
          </Panel>
        </Collapse>
      </div>
    </>
  );
};

export default EnvelopTrigger;
